// cache/issueCache.js
import { fetchIndex, fetchChanges } from '../api/issues';

/**
 * Singleton cache state for a specific scope
 */
class CacheState {
  /**
   * @param {import('../types/issues').Scope} scope
   */
  constructor(scope) {
    this.scope = scope;
    /** @type {import('../types/issues').NormalizedIssue[]} */
    this.list = [];
    /** @type {Map<string, import('../types/issues').NormalizedIssue>} */
    this.byId = new Map();
    /** @type {string | null} */
    this.cursor = null;
    this.loading = false;
    /** @type {string | null} */
    this.lastServerNow = null;
    /** @type {string | null} */
    this.lastDeltaSince = null;
    /** @type {AbortController | null} */
    this.aborter = null;
    this.queueVersion = 0; // bump to cancel background loop
    /** @type {Set<() => void>} */
    this.listeners = new Set();
    this.includeDone = true; // fetch ALL issues including Done (frontend can filter)
    this.deltaSyncInProgress = false; // lock to prevent concurrent delta syncs
    this.deltaTimerId = null; // timer ID for scheduled delta sync
  }

  /**
   * Subscribe to cache updates
   * @param {() => void} fn - Callback function
   * @returns {() => void} Unsubscribe function
   */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Notify all listeners of changes
   */
  notify() {
    for (const fn of this.listeners) fn();
  }

  /**
   * Upsert an issue into the cache
   * @param {import('../types/issues').NormalizedIssue} issue
   */
  upsert(issue) {
    const existing = this.byId.get(issue.id);
    if (existing) {
      // Merge fields and update reference
      Object.assign(existing, issue);
    } else {
      this.byId.set(issue.id, issue);
      this.list.push(issue); // append; we rely on server order initially
    }
  }

  /**
   * Remove an issue from cache by ID
   * @param {string} id - Issue ID
   */
  remove(id) {
    if (!this.byId.has(id)) return;
    this.byId.delete(id);
    const i = this.list.findIndex(x => x.id === id);
    if (i >= 0) this.list.splice(i, 1);
  }

  /**
   * Get an issue by key (e.g., 'PROJ-123')
   * @param {string} key - Issue key
   * @returns {import('../types/issues').NormalizedIssue | null}
   */
  getByKey(key) {
    return this.list.find(issue => issue.key === key) || null;
  }

  /**
   * Upsert an issue and notify listeners
   * @param {import('../types/issues').NormalizedIssue} issue
   */
  upsertAndNotify(issue) {
    this.upsert(issue);
    this.notify();
  }

  /**
   * Re-sort the list using server-provided sort metadata
   */
  resort() {
    this.list.sort((a, b) => {
      if (a.sort.group !== b.sort.group) return a.sort.group - b.sort.group;
      if (a.sort.primary !== b.sort.primary) return a.sort.primary - b.sort.primary;
      if (a.sort.priorityRank !== b.sort.priorityRank) return a.sort.priorityRank - b.sort.priorityRank;
      if (a.sort.updatedMs !== b.sort.updatedMs) return b.sort.updatedMs - a.sort.updatedMs;
      return (a.key || '').localeCompare(b.key || '');
    });
  }

  /**
   * Set auth token for API calls
   * @param {string} token - Authorization token
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Start loading issues (first page + background continuation)
   * @param {boolean} background - Continue loading in background
   */
  async start(background = true) {
    if (this.loading) return;
    if (!this.authToken) {
      console.error('[IssueCache] No auth token set');
      return;
    }
    this.loading = true;
    this.notify(); // Notify UI immediately that loading has started
    this.aborter = new AbortController();
    const v = ++this.queueVersion;

    try {
      // First page for instant paint
      console.log(`[IssueCache:${this.scope}] Fetching first page (100 items, includeDone=${this.includeDone})`);
      const fetchStart = performance.now();
      const first = await fetchIndex(this.scope, null, 100, this.includeDone, this.authToken, this.aborter.signal);
      const fetchDuration = performance.now() - fetchStart;
      console.log(`[IssueCache:${this.scope}] First page received:`, { items: first.items.length, hasCursor: !!first.cursor, total: this.list.length, fetchDuration: `${fetchDuration.toFixed(0)}ms` });

      const upsertStart = performance.now();
      this.cursor = first.cursor;
      this.lastServerNow = first.serverNow;
      for (const it of first.items) this.upsert(it);
      const upsertDuration = performance.now() - upsertStart;
      console.log(`[IssueCache:${this.scope}] Upsert complete:`, { duration: `${upsertDuration.toFixed(0)}ms`, totalIssues: this.list.length });

      const notifyStart = performance.now();
      this.notify();
      const notifyDuration = performance.now() - notifyStart;
      console.log(`[IssueCache:${this.scope}] Notify complete:`, { duration: `${notifyDuration.toFixed(0)}ms` });

      if (!background) {
        console.log(`[IssueCache:${this.scope}] Background loading disabled, stopping after first page`);
        return; // caller can choose to render first page only
      }

      // Continue in background until cap or end
      let pageNum = 2;
      while (this.cursor && this.queueVersion === v) {
        console.log(`[IssueCache:${this.scope}] Fetching page ${pageNum} (cursor exists, total so far: ${this.list.length})`);
        const page = await fetchIndex(this.scope, this.cursor, 500, this.includeDone, this.authToken, this.aborter.signal);

        // Log first 5 issue keys to detect duplicates
        const keys = page.items.slice(0, 5).map(it => it.key).join(', ');
        console.log(`[IssueCache:${this.scope}] Page ${pageNum} received:`, {
          items: page.items.length,
          hasCursor: !!page.cursor,
          total: this.list.length,
          firstKeys: keys
        });

        this.cursor = page.cursor;
        this.lastServerNow = page.serverNow;
        for (const it of page.items) this.upsert(it);
        this.notify();
        pageNum++;
      }

      console.log(`[IssueCache:${this.scope}] Loading complete. Total issues: ${this.list.length}, cursor: ${this.cursor}`);

      // Setup delta sync (every 5 minutes)
      this.lastDeltaSince = this.lastServerNow || new Date().toISOString();
      this.scheduleDelta(v);
    } catch (e) {
      if (e.name === 'AbortError') {
        console.log(`[IssueCache:${this.scope}] Loading aborted`);
        return;
      }
      console.error(`[IssueCache:${this.scope}] Start error:`, e);
    } finally {
      this.loading = false;
      this.notify(); // Notify UI that loading has finished
    }
  }

  /**
   * Schedule periodic delta sync
   * @param {number} v - Queue version to check if canceled
   */
  scheduleDelta(v) {
    const tick = async () => {
      if (this.queueVersion !== v) return; // canceled

      // Lock: prevent concurrent delta syncs
      if (this.deltaSyncInProgress) {
        console.log(`[IssueCache:${this.scope}] Delta sync already in progress, skipping`);
        this.deltaTimerId = setTimeout(tick, 5 * 60 * 1000);
        return;
      }

      this.deltaSyncInProgress = true;

      try {
        // Add 2-minute safety margin to catch boundary issues
        const SAFETY_MARGIN_MS = 2 * 60 * 1000;
        let since = this.lastDeltaSince;

        if (!since) {
          // First sync: go back 5 minutes
          since = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        } else {
          // Subtract 2 minutes from last timestamp for overlap
          const sinceMs = Date.parse(since) - SAFETY_MARGIN_MS;
          since = new Date(sinceMs).toISOString();
        }

        console.log(`[IssueCache:${this.scope}] Delta sync: fetching changes since ${since}`);
        const res = await fetchChanges(this.scope, since, 500, this.includeDone, this.authToken, this.aborter?.signal);

        console.log(`[IssueCache:${this.scope}] Delta sync: received ${res.items?.length || 0} changed, ${res.removed?.length || 0} removed`);

        // Update watermark to server's current time (NOT the "since" we used)
        this.lastDeltaSince = res.serverNow;

        // Upsert changed items (keeps existing sort values, doesn't recompute)
        for (const it of res.items) this.upsert(it);

        // Remove items that moved to Done
        if (Array.isArray(res.removed)) {
          for (const id of res.removed) this.remove(id);
        }

        // DON'T resort - this would break sorting by mixing old/new time references
        // The server already provides correct sort values for changed items
        this.notify();
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error(`[IssueCache:${this.scope}] Delta sync error:`, e);
        }
      } finally {
        this.deltaSyncInProgress = false;

        // Schedule next sync
        if (this.queueVersion === v) {
          this.deltaTimerId = setTimeout(tick, 5 * 60 * 1000); // 5 minutes
        }
      }
    };

    // Schedule first sync in 5 minutes
    this.deltaTimerId = setTimeout(tick, 5 * 60 * 1000);
  }

  /**
   * Manually trigger delta sync (e.g., after Jira operations)
   * Debounced to avoid multiple rapid calls
   * @param {number} delayMs - Delay before triggering (default: 2000ms)
   */
  triggerDeltaSync(delayMs = 2000) {
    // Lock check: don't trigger if sync already in progress
    if (this.deltaSyncInProgress) {
      console.log(`[IssueCache:${this.scope}] Delta sync in progress, skipping manual trigger`);
      return;
    }

    // Clear existing scheduled sync
    if (this.deltaTimerId) {
      clearTimeout(this.deltaTimerId);
      this.deltaTimerId = null;
    }

    console.log(`[IssueCache:${this.scope}] Manual delta sync triggered (${delayMs}ms delay)`);

    // Schedule immediate sync
    this.deltaTimerId = setTimeout(async () => {
      if (this.deltaSyncInProgress) return;

      this.deltaSyncInProgress = true;

      try {
        const SAFETY_MARGIN_MS = 2 * 60 * 1000;
        let since = this.lastDeltaSince;

        if (!since) {
          since = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        } else {
          const sinceMs = Date.parse(since) - SAFETY_MARGIN_MS;
          since = new Date(sinceMs).toISOString();
        }

        console.log(`[IssueCache:${this.scope}] Manual delta sync: fetching changes since ${since}`);
        const res = await fetchChanges(this.scope, since, 500, this.includeDone, this.authToken, this.aborter?.signal);

        console.log(`[IssueCache:${this.scope}] Manual delta sync: received ${res.items?.length || 0} changed, ${res.removed?.length || 0} removed`);

        this.lastDeltaSince = res.serverNow;

        for (const it of res.items) this.upsert(it);

        if (Array.isArray(res.removed)) {
          for (const id of res.removed) this.remove(id);
        }

        this.notify();
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error(`[IssueCache:${this.scope}] Manual delta sync error:`, e);
        }
      } finally {
        this.deltaSyncInProgress = false;

        // Resume regular schedule (5 minutes from now)
        const v = this.queueVersion;
        this.deltaTimerId = setTimeout(async () => {
          this.scheduleDelta(v);
        }, 5 * 60 * 1000);
      }
    }, delayMs);
  }

  /**
   * Refresh the cache (cancel existing loads, clear cache, restart)
   */
  async refresh() {
    // Cancel existing
    this.queueVersion++;
    this.aborter?.abort();

    // Clear delta sync timer
    if (this.deltaTimerId) {
      clearTimeout(this.deltaTimerId);
      this.deltaTimerId = null;
    }

    // Reset state but keep subscribers
    this.list = [];
    this.byId.clear();
    this.cursor = null;
    this.lastServerNow = null;
    this.lastDeltaSince = null;
    this.loading = false; // Reset loading so start() can proceed
    this.deltaSyncInProgress = false;
    this.notify(); // Notify immediately to update UI with empty list

    await this.start(true);
  }
}

/**
 * Singleton cache manager for all scopes
 */
class IssueCache {
  constructor() {
    /** @type {Record<import('../types/issues').Scope, CacheState>} */
    this.byScope = {
      my: new CacheState('my'),
      others: new CacheState('others'),
      all: new CacheState('all')
    };
  }

  /**
   * Get cache for a specific scope
   * @param {import('../types/issues').Scope} scope
   * @returns {CacheState}
   */
  get(scope) {
    return this.byScope[scope];
  }

  /**
   * Refresh a specific scope
   * @param {import('../types/issues').Scope} scope
   */
  refresh(scope) {
    return this.byScope[scope].refresh();
  }

  /**
   * Manually trigger delta sync for a scope (e.g., after Jira operations)
   * @param {import('../types/issues').Scope} scope
   * @param {number} delayMs - Delay before triggering (default: 2000ms)
   */
  triggerDeltaSync(scope, delayMs = 2000) {
    return this.byScope[scope].triggerDeltaSync(delayMs);
  }
}

export const issueCacheSingleton = new IssueCache();
